import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { PagePath } from '@monorepo/client-types';
import { useGetFeatureFlag } from '@monorepo/feature-flag/client';
import { FLAG } from '@monorepo/feature-flag/shared';
import { Application } from '@monorepo/types';
import { useCreditEventsModal } from '../../contexts/credit-events-modal';
import { useStore } from '../../helpers/use-store';
import { OnboardingAssetsModal } from './onboarding-assets-modal';

/**
 * Wrapper component that manages the logic for showing the onboarding assets modal.
 * It checks if the user has asset preferences and automatically opens the modal when needed.
 * Only renders if the feature flag is enabled.
 */
const OnboardingAssetsModalWrapper = observer(() => {
  const {
    dataStore: { userStore },
  } = useStore();
  const location = useLocation();
  const { hasActiveCreditEvents } = useCreditEventsModal();

  const isFeatureEnabled = useGetFeatureFlag(
    FLAG.ENABLE_ONBOARDING_ASSETS_MODAL,
    false,
    { env: Application.THE5ERS },
  );

  const shouldShowModal = useMemo(() => {
    if (!isFeatureEnabled || userStore.isImpersonated) {
      return false;
    }

    // Never show during payment confirmation screens
    if (
      location.pathname.startsWith(PagePath.AfterPurchase) ||
      new URLSearchParams(location.search).has('ThankYou')
    ) {
      return false;
    }

    const user = userStore.currentUser;
    if (!user?.userId) {
      return false;
    }

    // Anonymous users are mid-quick-purchase flow — skip the onboarding modal
    if (user.isAnonymous) {
      return false;
    }

    const hasPreference =
      user.assetPreference?.selectedAsset &&
      user.assetPreference.selectedAsset.length > 0;

    return !hasPreference;
  }, [
    isFeatureEnabled,
    location.pathname,
    location.search,
    userStore.currentUser,
    userStore.isImpersonated,
  ]);

  const [isOnboardingAssetsModalOpen, setIsOnboardingAssetsModalOpen] =
    useState(false);

  // Defer to credit events modal — highest-priority promo (see credit-events-modal-context).
  useEffect(() => {
    if (hasActiveCreditEvents) {
      setIsOnboardingAssetsModalOpen(false);
    }
  }, [hasActiveCreditEvents]);

  useEffect(() => {
    if (shouldShowModal && !hasActiveCreditEvents) {
      setIsOnboardingAssetsModalOpen(true);
    }
  }, [shouldShowModal, hasActiveCreditEvents]);

  // Don't render anything if feature flag is disabled
  if (!isFeatureEnabled) {
    return null;
  }

  return (
    <OnboardingAssetsModal
      open={isOnboardingAssetsModalOpen}
      onClose={() => setIsOnboardingAssetsModalOpen(false)}
      onContinue={() => setIsOnboardingAssetsModalOpen(false)}
    />
  );
});

export default OnboardingAssetsModalWrapper;
